// src/App.tsx
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { BrandingProvider } from '@/contexts/BrandingContext';
import { LogoProvider } from '@/contexts/LogoContext';
import { router } from '@/router';
import { toast } from 'sonner'; // Import toast for error notifications
import { AlertWebhookProvider } from '@/components/providers/AlertWebhookProvider';

// This component will initialize the Featurebase changelog widget.
const FeaturebaseChangelogInitializer: React.FC = () => {
  const { user, profile, session, loading: authLoading } = useAuth();

  useEffect(() => {
    // Wait for auth to be resolved and user to be logged in
    if (authLoading || !session || !user) {
      if (!authLoading && session && !user) {
          console.warn("FeaturebaseChangelogInitializer: Session exists, but user object is not yet available. Waiting.");
      }
      return;
    }

    // Ensure Featurebase SDK is available
    if (typeof window.Featurebase !== 'function') {
      console.warn("FeaturebaseChangelogInitializer: window.Featurebase is not a function. SDK might still be loading or failed.");
      // The script in index.html should make it available.
      // If this warning persists, there might be an issue with the SDK script itself.
      return;
    }
    
    const userName = profile?.full_name || user?.email || "Valued User";
    
    // IMPORTANT: Confirm 'scheduleheroai' is your correct Featurebase organization ID.
    // This ID was found in your RoadmapPage.tsx.
    const organizationId = "scheduleheroai"; 

    window.Featurebase("init_changelog_widget", {
      organization: organizationId,
      popup: {
        enabled: true,
        usersName: userName,
        autoOpenForNewUpdates: true,
      },
      dropdown: {
        enabled: false, 
      },
      // Pass user details for consistent identification if your Featurebase setup requires it
      // for tracking "seen" updates per user.
      user: {
        email: user.email,
        name: profile?.full_name ?? undefined, // Ensure name is string or undefined
        user_id: user.id, // A unique and stable user identifier
      },
      theme: "light",
      locale: "en",
    }, (error: any, data: any) => { // Added explicit 'any' types here
      if (error) {
        console.error("FeaturebaseChangelogInitializer: Error initializing changelog widget:", error);
        toast.error("Could not load latest updates. Please try again later.");
      }
      if (data) {
        console.log("FeaturebaseChangelogInitializer: Changelog widget event/data:", data);
        if (data.action === 'widgetReady') {
            console.log("FeaturebaseChangelogInitializer: Changelog widget is ready.");
        }
        if (data.action === 'unreadChangelogsCountChanged') {
          console.log('FeaturebaseChangelogInitializer: Unread changelogs count:', data.unreadCount);
        }
        // Note: The popup opening is managed by autoOpenForNewUpdates and Featurebase's internal logic.
        // This callback primarily confirms initialization.
      }
    });

  }, [user, profile, session, authLoading]); // Rerun if auth state or user details change

  return null; // This component does not render any UI itself
};

function App() { 
  return (
    <AuthProvider>
      <BrandingProvider>
        <LogoProvider>
          <AlertWebhookProvider>
          <RouterProvider router={router} />
          <FeaturebaseChangelogInitializer /> {/* Add the initializer component here */}
          </AlertWebhookProvider>
        </LogoProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;